import type { EChartsOption } from 'echarts';

import { graphConfig } from '@/configs/Graphs';
import { BehaviorChart } from '@/features/forecasts/components/order-behaviour/BehaviorChart';
import { useOrderData } from '@/features/forecasts/hooks/useOrderData';
import { buildTooltipSeriesLine, joinTooltipLines } from '@/features/forecasts/utils/forecastTooltip';
import { formatIsoDateMd } from '@/utils/date';
import { format } from '@/utils/format';
import { createNoDataOption } from '@/utils/graphs';
import { mostFrequent } from '@/utils/sort';

type Props = {
  chartGroup?: string;
};

export function OrderBehaviorLeadTime({ chartGroup }: Props) {
  const { records, total } = useOrderData();

  const { axisLabel, colors, grid } = graphConfig;
  const orderCount = format('Last {{total}} orders', { total });
  const { eligible, met } = records.reduce(
    (acc, record) => {
      const requested = record.order_minus_request_days;
      const committed = record.order_minus_promise_days;
      if (typeof requested !== 'number' || typeof committed !== 'number') return acc;
      acc.eligible += 1;
      if (committed <= requested) acc.met += 1;
      return acc;
    },
    { eligible: 0, met: 0 },
  );
  const otifPct = eligible > 0 ? (met / eligible) * 100 : null;
  const otifSummary = otifPct == null ? 'N/A' : `${otifPct.toFixed(1)}%`;
  const requestedDays = mostFrequent(records.map((r) => r.order_minus_request_days));
  const committedDays = mostFrequent(records.map((r) => r.order_minus_promise_days));
  const subtitle = format(
    'Most frequently requested: {{requestedDays}} days, most frequently committed: {{committedDays}} days • Average OTIF: {{otifSummary}} • {{orderCount}}',
    {
      requestedDays: requestedDays ?? 'N/A',
      committedDays: committedDays ?? 'N/A',
      otifSummary,
      orderCount,
    },
  );
  const committedLabel = 'Lead time';
  const shippedLabel = 'Shipped date';
  const hasLeadTimeData = records.some(
    (record) =>
      typeof record.order_minus_request_days === 'number' ||
      typeof record.order_minus_promise_days === 'number' ||
      typeof record.order_minus_ship_days === 'number',
  );

  // Anchor the y-axis on 0 (the requested date) with gridlines on multiples of 7. Bounds are rounded
  // outward to the nearest 7 so the ticks read …-14, -7, 0, 7, 14… instead of drifting off the data min.
  const plottedValues = records.flatMap((d) => [
    d.order_minus_request_days == null ? 0 : -d.order_minus_request_days,
    d.order_minus_ship_days ?? 0,
  ]);
  const yMin = Math.min(0, ...plottedValues);
  const yMax = Math.max(0, ...plottedValues);
  // Tick step: weekly when the span is small, otherwise the smallest multiple of
  // 7 that keeps the axis under ~10 gridlines. A fixed 7-day interval crowds the
  // labels into an unreadable smear once lead times stretch past a few months.
  const span = yMax - yMin || 7;
  const yAxisInterval = Math.max(7, Math.ceil(span / 10 / 7) * 7);
  const yAxisMin = Math.floor(yMin / yAxisInterval) * yAxisInterval;
  const yAxisMax = Math.ceil(yMax / yAxisInterval) * yAxisInterval;

  const chartOptions = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      /* c8 ignore start */
      formatter: (params) => {
        const items = (Array.isArray(params) ? params : [params]).filter(
          (item) => item?.seriesName === committedLabel || item?.seriesName === shippedLabel,
        );
        const first = items[0];
        const data = (first?.data ?? {}) as { timestamp?: string };
        const dateLabel = formatIsoDateMd(data.timestamp ?? '');
        const lines = items.map((item) => {
          const itemValue = (item.data as { value?: number } | undefined)?.value;
          const value = typeof itemValue === 'number' ? itemValue : null;
          return (
            value != null &&
            buildTooltipSeriesLine({ label: item.seriesName ?? '', marker: item.marker as string | undefined, value })
          );
        });
        return joinTooltipLines([dateLabel && buildTooltipSeriesLine({ label: 'Date', value: dateLabel }), ...lines]);
      },
      /* c8 ignore stop */
    },
    legend: {
      data: [committedLabel, shippedLabel],
      top: 0,
      right: 0,
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 8,
      textStyle: { fontSize: 11 },
    },
    grid,
    xAxis: {
      type: 'category',
      // Plotted against order sequence (0,1,2,…) so spacing is even regardless of
      // gaps between order dates; the date stays available in the tooltip.
      data: records.map((d) => `${d.order_sequence ?? ''}`),
      axisLabel,
    },
    yAxis: { type: 'value', axisLabel, interval: yAxisInterval, min: yAxisMin, max: yAxisMax },
    dataZoom: [{ type: 'slider', filterMode: 'none', bottom: 5, height: 20 }],
    series: [
      {
        // Lead time bar: negative, the order is requested ahead of the requested-date reference (y=0).
        name: committedLabel,
        type: 'bar',
        stack: 'leadTime',
        barWidth: '40%',
        // Requested date is the reference at y=0.
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: colors.actuals, type: 'dashed' },
          label: { show: false },
          data: [{ yAxis: 0 }],
        },
        data: records.map((d) => ({
          value: d.order_minus_request_days == null ? 0 : -d.order_minus_request_days,
          timestamp: d.date,
        })),
        itemStyle: { color: colors.actuals },
      },
      {
        // Shipped bar: positive relative to the requested date (raw order_minus_ship_days).
        name: shippedLabel,
        type: 'bar',
        stack: 'leadTime',
        barWidth: '40%',
        data: records.map((d) => ({
          value: d.order_minus_ship_days ?? 0,
          timestamp: d.date,
        })),
        itemStyle: { color: colors.actualsLight },
      },
    ],
  } satisfies EChartsOption;

  return (
    <BehaviorChart
      chartGroup={chartGroup}
      option={total > 0 && hasLeadTimeData ? chartOptions : createNoDataOption('No data available')}
      title={'Order Lead Times'}
      titleInfo={subtitle}
    />
  );
}
